import { Component } from '@theme/component';

/**
 * A small utility for setting up an animated disclosure panel using inert and a measured height animation.
 *
 * This component works with any element that follows the details/summary pattern for revealing content.
 * Forms and other interactive elements don't need to live inside a details element, but can still use
 * the expanded pattern for accessibility and animation.
 *
 * The component measures the content and animates an explicit `height` (plus the `inert` attribute for
 * accessibility) to create smooth expand/collapse transitions. A height animation is used instead of a
 * CSS grid `fr` track because Safari does not reliably resolve flexible grid tracks in an auto-height
 * container, which left the panel collapsed to 0 on real iOS devices.
 *
 * @class DisclosureCustom
 * @typedef {object} Refs
 * @property {HTMLElement} disclosureTrigger – The disclosure trigger element.
 * @property {HTMLElement} disclosureContent – The disclosure content element.
 *
 * @extends Component<Refs>
 */
class DisclosureCustom extends Component {
  requiredRefs = ['disclosureTrigger', 'disclosureContent'];

  connectedCallback() {
    super.connectedCallback();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  /**
   * Toggles the disclosure panel open/closed state.
   * Updates aria-expanded, aria-label attributes on the trigger and inert state on content.
   *
   * Animates the panel by measuring and transitioning an explicit `height` rather than relying on
   * CSS grid `fr` tracks, which Safari does not resolve reliably in an auto-height container.
   */
  toggleDisclosure = () => {
    const { disclosureTrigger: trigger, disclosureContent: content } = this.refs;
    const expanded = trigger.matches('[aria-expanded="true"]');
    const willExpand = !expanded;

    trigger.setAttribute('aria-expanded', String(willExpand));
    trigger.setAttribute(
      'aria-label',
      `${expanded ? trigger.dataset.disclosureOpen : trigger.dataset.disclosureClose}`
    );

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reduceMotion) {
      if (willExpand) {
        content.removeAttribute('inert');
      } else {
        content.setAttribute('inert', '');
      }
      return;
    }

    // scrollHeight reports the full content height in both the collapsed (height: 0, clipped) and
    // expanded (height: auto) states, so it is the correct animation target either way.
    const targetHeight = content.scrollHeight;

    if (willExpand) {
      content.style.height = '0px';
      content.removeAttribute('inert');
      // Force a reflow so the browser registers the 0px starting point before transitioning.
      void content.offsetHeight;
      content.style.height = `${targetHeight}px`;
    } else {
      content.style.height = `${targetHeight}px`;
      void content.offsetHeight;
      content.style.height = '0px';
      content.setAttribute('inert', '');
    }

    // Clear the inline height once the height transition settles so the panel can size to its
    // content again (e.g. when a discount pill is added while it is open). Only react to the
    // `height` transition — `opacity` transitions on the same element too.
    const onTransitionEnd = (/** @type {TransitionEvent} */ event) => {
      if (event.propertyName !== 'height') return;
      content.style.height = '';
      content.removeEventListener('transitionend', onTransitionEnd);
    };
    content.addEventListener('transitionend', onTransitionEnd);
  };
}

if (!customElements.get('disclosure-custom')) {
  customElements.define('disclosure-custom', DisclosureCustom);
}
